import {Component, Inject, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {ProleisSuppliersState} from '../../store/store.index';
import {OrdersActions, OrdersSelectors} from '../../store/orders/orders.index';
import {ActivatedRoute} from '@angular/router';
import {PositionActions, PositionSelectors} from '../../store/positions/position.index';
import {DeliveriesActions, DeliveriesSelectors} from '../../store/deliveries/deliveries.index';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {CreatePlannedDeliveryComponent} from '../overlays/create-planned-delivery/create.planned.delivery.component';
import {CreateDeliveryNoteComponent} from '../overlays/create-delivery-note/create.delivery.note.component';
import {APP_GUIDS, AppGuids} from '../../../app.guids';
import {EditPlannedDeliveryComponent} from '../overlays/edit-planned-delivery/edit.planned.delivery.component';

import * as Highcharts from 'highcharts';
import {Options} from 'highcharts';
import {combineLatest, iif, Observable, of, Subject} from 'rxjs';
import {FormControl} from '@angular/forms';
import {debounceTime, exhaustMap, startWith, tap} from 'rxjs/operators';
import {DatePipe} from '@angular/common';
import {SuppliersService} from '../../../services/suppliers.service';
import {SearchPositionDialogComponent} from '../search-position/search.position.dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {DocumentService, ProleisObject} from "@proleis/proleis-rest-client";

@Component({
  styleUrls: ['planned.deliveries.component.scss'],
  templateUrl: 'planned.deliveries.component.html',
  selector: 'ps-planned-deliveries'
})
export class PlannedDeliveriesComponent implements OnInit {

  Highcharts: typeof Highcharts = Highcharts;

  destroy$: Subject<any> = new Subject<any>();

  searchFormControl: FormControl;

  pos$: Observable<ProleisObject> = this.store.select(PositionSelectors.selectPosition);
  pbo$: Observable<ProleisObject> = this.store.select(OrdersSelectors.selectOrder);
  pos: ProleisObject;

  sumPlannedCount$: Observable<number> = of(0);
  sumConfirmedCount$: Observable<number> = of(0);
  sumRejectedCount$: Observable<number> = of(0);
  sumDeliveryCount$: Observable<number> = of(0);

  deliveries$: Observable<ProleisObject[]> = this.store.select(DeliveriesSelectors.SELECT_PLANNED_DELIVERIES);
  deliveriesAreLoading$: Observable<boolean> = this.store.select(DeliveriesSelectors.SELECT_PLANNED_DELIVERIES_ARE_LOADING);
  chartOptions$: Observable<Options> = this.store.select(DeliveriesSelectors.SELECT_DELIVERY_CHART_OPTIONS);
  isChartLoading$: Observable<boolean> = this.store.select(DeliveriesSelectors.SELECT_DELIVERY_CHART_IS_LOADING);
  icons$: Observable<Map<string, string>> = this.store.select(DeliveriesSelectors.SELECT_DELIVERY_STATUS_ICONS);

  chartOptions: Options;
  isChartLoading: boolean;

  partsstockExt: Observable<number>;
  partsstockUm: Observable<number>;


  constructor(private store: Store<ProleisSuppliersState>, private route: ActivatedRoute, private bs: MatBottomSheet,
              private documentsService: DocumentService, private supplierService: SuppliersService,
              @Inject(APP_GUIDS) private guids: AppGuids, private datePipe: DatePipe, private matDiag: MatDialog) {
  }

  ngOnInit() {

    this.searchFormControl = new FormControl();

    this.store.dispatch(PositionActions.LOAD_POSITION({pboId: this.route.snapshot.params.id, posId: this.route.snapshot.params.posId}));
    this.store.dispatch(OrdersActions.LOAD_ORDER({orderId: this.route.snapshot.params.id}));
    this.store.dispatch(DeliveriesActions.LOAD_PLANNED_DELIVERIES({
      orderId: this.route.snapshot.params.id,
      posId: this.route.snapshot.params.posId
    }));

    this.pos$.subscribe(pos => this.pos = pos);
    this.chartOptions$.subscribe(options => {
      this.chartOptions = options;
    });

    this.isChartLoading$.subscribe(isLoading => this.isChartLoading = isLoading);

    this.deliveries$ = combineLatest([this.deliveries$, this.filter()]).pipe(
      exhaustMap(([deliveries, filterExpression]) => {
        return iif(() => filterExpression === '',
          of(deliveries),
          of(deliveries.filter(
            delivery => {
              if (!filterExpression || filterExpression.length === 0) {
                return true;
              }

              let matched = false;
              if (delivery.DELIVERYNOTE_NR) {
                matched = delivery.DELIVERYNOTE_NR.toLowerCase().includes(filterExpression.toLowerCase());
              }

              if (!matched && delivery.PBO_DELIVERY_NAME) {
                matched = delivery.PBO_DELIVERY_NAME.toLowerCase().includes(filterExpression.toLowerCase());
              }

              if (!matched && delivery.PBOSUBTYPENAME) {
                matched = delivery.PBOSUBTYPENAME.toLowerCase().includes(filterExpression.toLowerCase());
              }

              if (!matched && delivery.FREIGABE_NAME) {
                matched = delivery.FREIGABE_NAME.toLowerCase().includes(filterExpression.toLowerCase());
              }

              if (!matched && delivery.PLANNED_DATE) {
                matched = this.datePipe.transform(new Date(delivery.PLANNED_DATE)).includes(filterExpression.toLowerCase());
              }

              return matched;
            })
          ));
      })
    );
    this.sumPlannedCount$ = this.getSumPlannedCount();
    this.sumDeliveryCount$ = this.getSumDeliveryCount();
    this.sumConfirmedCount$ = this.getSumConfirmedCount();
    this.sumRejectedCount$ = this.getSumRejectedCount();

    this.partsstockExt = this.supplierService.getPartsstock(this.route.snapshot.params.id, this.route.snapshot.params.posId, true);
    this.partsstockUm = this.supplierService.getPartsstock(this.route.snapshot.params.id, this.route.snapshot.params.posId, false);
  }

  openCreatePlannedDeliveryBottomsheet() {
    this.bs.open(CreatePlannedDeliveryComponent, {
      data: {
        pboId: this.route.snapshot.params.id,
        pseId: this.route.snapshot.params.posId
      }
    }).afterDismissed().subscribe(result => {
      console.log(result);
    });
  }

  openEditPlannedDeliveryBottomsheet(delivery: ProleisObject) {
    this.bs.open(EditPlannedDeliveryComponent, {
      data: {
        pboId: this.route.snapshot.params.id,
        posId: this.route.snapshot.params.posId,
        delivery
      }
    }).afterDismissed().subscribe(updatedDelivery => {
      console.log('update', updatedDelivery);
      this.store.dispatch(DeliveriesActions.UPDATE_PLANNED_DELIVERY({
        orderId: this.route.snapshot.params.id,
        posId: this.route.snapshot.params.posId,
        plannedDate: updatedDelivery.plannedDate,
        plannedCount: updatedDelivery.plannedCount,
        deliveryId: delivery.PLANNED_DELIVERY_ID,
        deliveryComment: updatedDelivery.deliveryComment,
        incidentId: updatedDelivery.incidentId
      }));
    });
  }

  createDeliveryNote(delivery: ProleisObject) {
    this.bs.open(CreateDeliveryNoteComponent, {data: {delivery}})
      .afterDismissed().subscribe(bottomSheetData => {
      if (bottomSheetData) {
        this.store.dispatch(DeliveriesActions.CREATE_DELIVERYNOTE({
          orderId: this.route.snapshot.params.id,
          posId: this.route.snapshot.params.posId,
          deliveryNoteNumber: bottomSheetData.deliveryNr,
          deliveryCount: bottomSheetData.count,
          deliveryDate: bottomSheetData.date,
          deliveryComment: bottomSheetData.deliveryComment,
          deliveryId: delivery.PLANNED_DELIVERY_ID
        }));
      }
    });
  }

  cancelDelivery(noteId: string) {
    this.store.dispatch(DeliveriesActions.CANCEL_DELIVERY({
      deliveryNoteId: noteId,
      orderId: this.route.snapshot.params.id,
      pseId: this.route.snapshot.params.posId
    }));
  }

  checkOpen(delivery: ProleisObject): boolean {
    return delivery.FREIGABE_GUID === this.guids.GUID_OPEN;
  }

  checkDelivered(delivery: ProleisObject): boolean {
    return delivery.FREIGABE_GUID === this.guids.GUID_DELIVERED;
  }

  checkCanceled(delivery: ProleisObject): boolean {
    return delivery.FREIGABE_GUID === this.guids.GUID_CANCELED;
  }

  hasIndexAi(): boolean {
    return !!this.pos.INDEX_AI;
  }

  onTabSelectionChanged(event) {
    if (event.index === 1) {
      this.store.dispatch(DeliveriesActions.LOAD_DELIVERIES_CHART({
        orderId: this.route.snapshot.params.id,
        posId: this.route.snapshot.params.posId
      }));
    }
  }

  deleteDelivery(delivery: ProleisObject) {
    this.store.dispatch(DeliveriesActions.DELETE_PLANNED_DELIVERY({
      orderId: this.route.snapshot.params.id,
      posId: this.route.snapshot.params.posId,
      deliveryId: delivery.PLANNED_DELIVERY_ID
    }));
  }

  getBackground(delivery: ProleisObject): string {
    if (this.checkDelivered(delivery)) {
      return 'status-green';
    }
    if (this.checkCanceled(delivery)) {
      return 'status-red';
    }
    if (this.checkOpen(delivery)) {
      return 'status-blue';
    }
    if (!delivery.FREIGABE_GUID) {
      return 'status-grey';
    }
  }

  filter(): Observable<string> {
    return this.searchFormControl.valueChanges.pipe(
      tap(value => localStorage.setItem('ps-deliveries-search', value)),
      debounceTime(250),
      startWith(localStorage.getItem('ps-deliveries-search'))
    );
  }

  getSumPlannedCount(): Observable<number> {
    return combineLatest([this.sumPlannedCount$, this.deliveries$]).pipe(
      exhaustMap(([sumPlanned, delivieries]) => {
        let sum = 0;
        delivieries.forEach(note => {
          sum += note.PLANNED_COUNT;
        });
        return of(sum);
      })
    );
  }

  getSumDeliveryCount(): Observable<number> {
    return combineLatest([this.sumPlannedCount$, this.deliveries$]).pipe(
      exhaustMap(([sumPlanned, delivieries]) => {
        let sum = 0;
        delivieries.forEach(note => {
          sum += note.DELIVERYNOTE_COUNT;
        });
        return of(sum);
      })
    );
  }

  getSumRejectedCount(): Observable<number> {
    return combineLatest([this.sumPlannedCount$, this.deliveries$]).pipe(
      exhaustMap(([sumPlanned, delivieries]) => {
        let sum = 0;
        delivieries.forEach(note => {
          if (this.checkCanceled(note)) {
            sum += note.DELIVERYNOTE_COUNT;
          }
        });
        return of(sum);
      })
    );
  }

  getSumConfirmedCount(): Observable<number> {
    return combineLatest([this.sumPlannedCount$, this.deliveries$]).pipe(
      exhaustMap(([sumPlanned, delivieries]) => {
        let sum = 0;
        delivieries.forEach(note => {
          if (this.checkDelivered(note)) {
            sum += note.PLANNED_COUNT;
          }
        });
        return of(sum);
      })
    );
  }

  openSearchDialog() {
    this.matDiag.open(SearchPositionDialogComponent);
  }
}
